import { useQuery } from '@vue/apollo-composable';
import dayjs from 'dayjs';
import { useUserStore } from '@/store/userStore';
import { formatCurrency } from '@/utils/currency';
import {
  SCHOOL_AI_USAGE,
  type SchoolAiUsage,
  type SchoolAiUsageResult,
} from '@/graphql/aiAssistant';

/** Percentage of the monthly cap at which we start warning (calmly). */
export const AI_USAGE_WARNING_THRESHOLD = 80;

export type AiUsageStatus = 'ok' | 'warning' | 'reached' | 'uncapped';

const formatTokens = (value: number) => new Intl.NumberFormat('en-US').format(value);

/**
 * School-facing AI spend/usage snapshot, shared by the Settings panel and the
 * compact dashboard card so both phrase the SAME derived state.
 *
 * Everything here is read-only by design: a school admin cannot change its own
 * cap (only the platform can), so this composable exposes no mutation and the
 * surfaces built on it must not offer a control that would fail.
 *
 * Degradation contract: `School.aiUsage` ships in a separate backend PR
 * (sms-be#312), and resolves to `null` for non-admins. A missing field, a
 * GraphQL error, or a null payload all collapse to `usage === null`, which every
 * host renders as NOTHING — no empty card, no error banner.
 */
export function useSchoolAiUsage() {
  const userStore = useUserStore();

  // Same cast the router guard / useAiAssistantAccess use: the generated `me`
  // union doesn't carry __typename on every branch even though the wire does.
  const isStaff = computed(
    () => (userStore.currentUser as { __typename?: string } | null)?.__typename === 'User',
  );

  // Students can't call getSchool at all, so never fire it for them.
  // errorPolicy 'all' keeps a schema-validation failure (backend without the
  // field) as a quiet null instead of an unhandled rejection.
  const { result, loading, refetch } = useQuery<SchoolAiUsageResult>(
    SCHOOL_AI_USAGE,
    null,
    () => ({ enabled: isStaff.value, errorPolicy: 'all' }),
  );

  const usage = computed<SchoolAiUsage | null>(() => result.value?.getSchool?.aiUsage ?? null);

  /** The single gate every host uses: no snapshot -> render nothing. */
  const hasUsage = computed(() => usage.value !== null);

  const currency = computed(() => usage.value?.currency ?? '');

  /** Cap `null` (NOT 0) means the school has no ceiling. */
  const isUncapped = computed(() => !!usage.value && usage.value.monthlyCap === null);
  const hasDailyCap = computed(() => !!usage.value && usage.value.dailyCap !== null);

  const capReached = computed(() => !!usage.value?.capReached);
  const hasUnpricedUsage = computed(() => !!usage.value?.hasUnpricedUsage);

  function percentOf(spend: number | undefined, cap: number | null | undefined) {
    if (cap === null || cap === undefined || spend === undefined) return 0;
    // A cap of 0 means "no spend allowed" — it is fully consumed by definition,
    // and dividing by it would produce NaN/Infinity.
    if (cap <= 0) return 100;
    return Math.min(100, Math.max(0, Math.round((spend / cap) * 100)));
  }

  const monthlyPercent = computed(() => percentOf(usage.value?.monthlySpend, usage.value?.monthlyCap));
  const dailyPercent = computed(() => percentOf(usage.value?.dailySpend, usage.value?.dailyCap));

  const isApproachingCap = computed(
    () => hasUsage.value
      && !isUncapped.value
      && !capReached.value
      && monthlyPercent.value >= AI_USAGE_WARNING_THRESHOLD,
  );

  const status = computed<AiUsageStatus>(() => {
    if (!hasUsage.value) return 'ok';
    // A reached cap is the most actionable state, so it outranks "uncapped"
    // even in the (contradictory) case where the backend reports both.
    if (capReached.value) return 'reached';
    if (isUncapped.value) return 'uncapped';
    return isApproachingCap.value ? 'warning' : 'ok';
  });

  /** Format with the currency the API returned — there is no FX conversion. */
  function money(value: number | null | undefined) {
    if (value === null || value === undefined || !currency.value) return '';
    return formatCurrency(value, currency.value);
  }

  const monthlySpendLabel = computed(() => money(usage.value?.monthlySpend));
  const monthlyCapLabel = computed(() => money(usage.value?.monthlyCap));
  const monthlyRemainingLabel = computed(() => money(usage.value?.monthlyRemaining));
  const monthlyTokensLabel = computed(() => formatTokens(usage.value?.monthlyTokens ?? 0));

  const dailySpendLabel = computed(() => money(usage.value?.dailySpend));
  const dailyCapLabel = computed(() => money(usage.value?.dailyCap));
  const dailyRemainingLabel = computed(() => money(usage.value?.dailyRemaining));
  const dailyTokensLabel = computed(() => formatTokens(usage.value?.dailyTokens ?? 0));

  /** e.g. "1 – 31 Jul 2026"; falls back to the raw strings if unparseable. */
  const periodLabel = computed(() => {
    const u = usage.value;
    // dayjs(undefined) silently means "now" — never let that reach the UI.
    if (!u || !u.periodStart || !u.periodEnd) return '';
    const start = dayjs(u.periodStart);
    const end = dayjs(u.periodEnd);
    if (!start.isValid() || !end.isValid()) return `${u.periodStart} – ${u.periodEnd}`;
    const sameYear = start.year() === end.year();
    return `${start.format(sameYear ? 'D MMM' : 'D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
  });

  /** Tailwind class for the progress fill, keyed off the same status. */
  const barClass = computed(() => {
    if (capReached.value) return 'bg-rose-500';
    if (isApproachingCap.value) return 'bg-amber-500';
    return 'bg-primary';
  });

  return {
    usage,
    hasUsage,
    loading,
    refetch,
    currency,
    status,
    isUncapped,
    hasDailyCap,
    capReached,
    isApproachingCap,
    hasUnpricedUsage,
    monthlyPercent,
    dailyPercent,
    monthlySpendLabel,
    monthlyCapLabel,
    monthlyRemainingLabel,
    monthlyTokensLabel,
    dailySpendLabel,
    dailyCapLabel,
    dailyRemainingLabel,
    dailyTokensLabel,
    periodLabel,
    barClass,
  };
}
